/**
 * Public artist directory API.
 *
 * Mirrors the engine's GET /api/artists and /api/artists/:id exactly. Neither needs a
 * session, so nothing here assumes one.
 *
 * TWO ABSENCES ARE DELIBERATE and are why these types are written out rather than reused
 * from lib/artist.ts, which describes the artist's own view of themselves:
 *
 *   `reliability` is OPTIONAL. The engine omits the whole block until an artist has five
 *   performed events, so a type that promised it would invite a component to read
 *   through a value that is not there. `buildingTrackRecord` is the flag to branch on.
 *
 *   The breakdown has three fields, not six. Sell-through accuracy, promotional score and
 *   the average organizer rating stay private to the artist; the engine does not send
 *   them on this surface and declaring them here would imply they exist to render.
 */

import { api } from './apiClient';

export interface ArtistCard {
  artistId: string;
  stageName: string;
  genre: string | null;
  primaryMarket: string | null;
  profileImageUrl: string | null;
  tier: string;
  /** Truncated server-side; `bioTruncated` says whether there is more to read. */
  bioSnippet: string | null;
  bioTruncated: boolean;
  followers: number;
  /** Null until the artist passes the visibility threshold. */
  reliabilityScore: number | null;
  buildingTrackRecord: boolean;
}

export interface ArtistDate {
  eventId: string;
  title: string;
  eventDate: string | null;
  venueName: string | null;
  venueCity: string | null;
}

export interface ArtistReliability {
  overallScore: number;
  eventsPerformed: number;
  eventsBooked: number;
  /** Conduct only. See the note at the top of this file. */
  breakdown: {
    showUpRate: number;
    onTimeRate: number;
    contractComplianceRate: number;
  };
  weights: { showUp: number; onTime: number; compliance: number };
  lastCalculatedAt: string | null;
}

export interface ArtistPublicProfile {
  artistId: string;
  stageName: string;
  bio: string | null;
  genre: string | null;
  primaryMarket: string | null;
  profileImageUrl: string | null;
  coverImageUrl: string | null;
  tier: string;
  social: {
    instagram: { handle: string | null; followers: number | null };
    tiktok: { handle: string | null; followers: number | null };
    spotifyMonthlyListeners: number | null;
    youtubeSubscribers: number | null;
  };
  followers: number;
  reliabilityScore: number | null;
  buildingTrackRecord: boolean;
  upcomingDates: ArtistDate[];
  pastDates: ArtistDate[];
  /** Absent entirely below the threshold. */
  reliability?: ArtistReliability;
  visibilityThreshold: number;
}

export interface ArtistFilters {
  genre?: string;
  market?: string;
  tier?: string;
}

export function listArtists(filters: ArtistFilters = {}): Promise<ArtistCard[]> {
  const params = new URLSearchParams();
  if (filters.genre) params.set('genre', filters.genre);
  if (filters.market) params.set('market', filters.market);
  if (filters.tier) params.set('tier', filters.tier);
  const qs = params.toString();
  return api.get<ArtistCard[]>(`/artists${qs ? `?${qs}` : ''}`);
}

export function getArtist(artistId: string): Promise<ArtistPublicProfile> {
  return api.get<ArtistPublicProfile>(`/artists/${artistId}`);
}

// ── Following ───────────────────────────────────────────────────────────────

interface FollowingResponse {
  artists: { artistId: string; stageName: string }[];
}

/**
 * Whether the signed-in buyer already follows this artist.
 *
 * The engine has no "am I following X" endpoint, so this reads the buyer's own follow
 * list and checks membership. Buyer-only and authenticated, so callers must not invoke
 * it for a guest or another role: the engine answers 401 or 403, which is correct but
 * noisy. The profile page checks the role first.
 */
export async function isFollowing(artistId: string): Promise<boolean> {
  const result = await api.get<FollowingResponse>('/following');
  return result.artists.some((a) => a.artistId === artistId);
}

export function followArtist(artistId: string): Promise<unknown> {
  return api.post(`/follow/${artistId}`);
}

export function unfollowArtist(artistId: string): Promise<unknown> {
  return api.delete(`/follow/${artistId}`);
}
